import './NftList.sass';

import React, { FC, useCallback, useEffect, useState } from 'react';
import { Button, Header, Placeholder, ScrollView, Address, ActionSheet } from '$uikit';
import { useCurrentUserInfo, useTranslator } from '$hooks';
import { Api } from '$api';
import { NftItem } from './NftItem/NftItem';
import { NftModel } from '$store/models';
import { DiamondsSubscriptionSheet, Loader } from '$shared/components';
import { goTo } from '$navigation/helper';
import { useDispatch, useSelector } from 'react-redux';
import { mainActions, mainSelector } from '$store/main';
import { toastActions } from '$store/toast';
import { PROFILE_REDIRECT, RESOLVE } from '$shared/constants/pages';
import { IcMore24 } from '$assets';
import { authActions } from '$store/auth';
import { _confirm } from '$utils';

export const NftList: FC = () => {
  const [items, setItems] = useState<NftModel[]>([]);
  const [pending, setPending] = useState(true);
  const currentUser = useCurrentUserInfo();
  const [isActionShow, setActionsShown] = useState(false);
  const dispatch = useDispatch();
  const { tonAddress } = useSelector(mainSelector);
  const hasNft = !!items.length;
  const t = useTranslator();

  useEffect(() => {
    Api.get('/nft/my')
      .then((data: any) => {
        const { nftItems, canSubscribe } = data as {
          nftItems: NftModel[];
          canSubscribe: boolean;
        };
        setItems(nftItems);
        if (canSubscribe) {
          dispatch(mainActions.setPopup(<DiamondsSubscriptionSheet />));
        }
      })
      .finally(() => {
        setPending(false);
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(e.message));
      });
  }, [dispatch]);

  const handleDisconnect = () => {
    _confirm(t('nft_list_disconnect_wallet_confirm'), () => {
      dispatch(toastActions.setToastLoading());
      Api.post('/nft/remove_address')
        .then(() => {
          dispatch(mainActions.setTonAddress(''));
          dispatch(authActions.init()); // Hack to update decryptKeys
          goTo(PROFILE_REDIRECT);
          dispatch(toastActions.hideToast());
        })
        .catch((e) => {
          dispatch(toastActions.setToastFail(e.message));
        });
    });
  };

  const handleActionChange = useCallback(
    (value?: string) => {
      setActionsShown(false);
      if (value === 'disconnect') {
        handleDisconnect();
      }
    },
    [handleDisconnect],
  );

  const handleNftChange = (nft: NftModel) => () => {
    dispatch(toastActions.setToastLoading());
    Api.post('/nft/profile/photo', {
      address: nft.address,
    })
      .then((data) => {
        const items = data as unknown as NftModel[];
        setItems(items);
        goTo(RESOLVE, { domain: currentUser.domain || `id${currentUser.id}` });
      })
      .finally(() => {
        dispatch(toastActions.hideToast());
      })
      .catch((e) => {
        dispatch(toastActions.setToastFail(t('nft_photo_change_fail')));
      });
  };

  return (
    <>
      <Header
        showBackButton
        rightIcons={
          hasNft
            ? [
                {
                  icon: <IcMore24 />,
                  onClick: () => setActionsShown(true),
                },
              ]
            : undefined
        }
      >
        <div className="NftList__header_title_wrapper">
          <div className="NftList__header_title">{t('nft_list_title')}</div>
          <Address canCopy className="NftList__header_address" address={tonAddress} />
        </div>
      </Header>
      <ScrollView>
        {!pending ? (
          hasNft ? (
            <div className="NftList__items">
              {items.map((item) => (
                <NftItem key={item.address} onClick={handleNftChange(item)} item={item} />
              ))}
            </div>
          ) : (
            <Placeholder
              centered
              title={t('nft_list_placeholder_title')}
              actions={
                <Button mode="secondary" size="small" onClick={handleDisconnect}>
                  {t('nft_list_placeholder_disconnect_wallet')}
                </Button>
              }
            >
              <Address canCopy address={tonAddress} />
            </Placeholder>
          )
        ) : (
          <Loader isWrapped />
        )}
      </ScrollView>
      <ActionSheet
        isOpened={isActionShow}
        options={[
          { label: t('nft_list_placeholder_disconnect_wallet'), value: 'disconnect' },
        ]}
        onChange={handleActionChange}
      />
    </>
  );
};
